/*
 |--------------------------------------------------------------------------
 | ACarousel Module
 |
 | This module adds functionality to the Carousel component
 |--------------------------------------------------------------------------
 */

// Constants
const ACarouselSelector = '.a-carousel';
const ACarouselViewportSelector = '.a-carousel__viewport';
const ACarouselTrackSelector = '.a-carousel__track';
const ACarouselItemSelector = '.a-carousel__item';
const ACarouselPrevSelector = '.a-carousel__prev';
const ACarouselNextSelector = '.a-carousel__next';
const ACarouselStatusSelector = '[data-carousel-status]';
const ACarouselScrollDirectionLeft = -1;
const ACarouselScrollDirectionRight = 1;

// Class
class ACarousel {
  element;
  viewport;
  items;
  prev;
  next;
  statusIndicator;
  step;
  scrollTimeout;
  supportsSnap;
  reducedMotion;
  tolerance = 20; // Tolerance for browser snap behavior

  // Drag state
  isDragging = false;
  startX = 0;
  scrollLeftStart = 0;

  constructor(element) {
    this.element = element;
    this.supportsSnap = CSS.supports('scroll-snap-type: x mandatory');
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  /* ---------------------------------------------------------------------
   * Init
   * ------------------------------------------------------------------ */

  init() {
    this.cacheElements();
    this.setupAria();
    this.bindEvents();
    this.updateContainerOffset();
    this.updateButtons();
    this.updateItemAccessibility(this.getCurrentIndex());
    this.announce();
  }

  cacheElements() {
    this.viewport = this.element.querySelector(ACarouselViewportSelector);
    this.items = [...this.element.querySelectorAll(ACarouselItemSelector)];
    this.prev = this.element.querySelector(ACarouselPrevSelector);
    this.next = this.element.querySelector(ACarouselNextSelector);
    this.statusIndicator = this.element.querySelector(ACarouselStatusSelector);
    this.step = Number(this.element.dataset.step || 1);
  }

  bindEvents() {
    this.prev?.addEventListener('click', () =>
      this.scrollByStep(ACarouselScrollDirectionLeft)
    );
    this.next?.addEventListener('click', () =>
      this.scrollByStep(ACarouselScrollDirectionRight)
    );

    this.viewport.addEventListener('scroll', this.onScroll);

    this.items.forEach((item, index) => {
      item.addEventListener('keydown', (e) => this.onItemKeydown(e));
      item.addEventListener('focus', () => this.onItemFocus(index));
    });

    // Recalculate container offset on resize
    window.addEventListener('resize', this.onResize);

    // Only bind drag events if carousel is draggable
    if (this.element.classList.contains('a-carousel--draggable')) {
      // Mouse drag events
      this.viewport.addEventListener('mousedown', this.onDragStart);
      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragEnd);

      // Touch drag events
      this.viewport.addEventListener('touchstart', this.onDragStart, {
        passive: true,
      });
      this.viewport.addEventListener('touchmove', this.onDragMove, {
        passive: false,
      });
      this.viewport.addEventListener('touchend', this.onDragEnd);
    }
  }

  getItemWidth(index) {
    const item = this.items[index];
    if (!item) return 0;
    return (
      item.getBoundingClientRect().width +
      parseFloat(getComputedStyle(item).marginRight || 0)
    );
  }

  getItemScrollPosition(index) {
    let scrollPos = 0;
    for (let i = 0; i < index; i++) {
      scrollPos += this.getItemWidth(i);
    }
    return scrollPos;
  }

  getCurrentIndex() {
    let scrollLeft = this.viewport.scrollLeft;
    let currentPos = 0;

    for (let i = 0; i < this.items.length; i++) {
      const itemWidth = this.getItemWidth(i);
      const itemEnd = currentPos + itemWidth;

      if (scrollLeft < itemEnd) {
        return i;
      }

      currentPos = itemEnd;
    }

    return Math.max(0, this.items.length - 1);
  }

  scrollByStep(direction) {
    const current = this.getCurrentIndex();
    const doStep = current + direction * this.step;
    const targetIndex = Math.max(0, Math.min(this.items.length - 1, doStep));

    const targetScrollPos = this.getItemScrollPosition(targetIndex);
    this.animateScrollTo(targetScrollPos);
    this.updateItemAccessibility(targetIndex);
    this.focusItem(targetIndex);
    this.announce();
  }

  focusItem(index) {
    this.items[index]?.focus({ preventScroll: true });
  }

  updateButtons() {
    if (this.prev) {
      this.prev.disabled = this.isAtStart();
      this.prev.setAttribute('aria-disabled', String(this.prev.disabled));
    }
    if (this.next) {
      this.next.disabled = this.isAtEnd();
      this.next.setAttribute('aria-disabled', String(this.next.disabled));
    }
  }

  isAtStart() {
    return this.viewport.scrollLeft <= this.tolerance;
  }

  isAtEnd() {
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    return this.viewport.scrollLeft >= maxScroll - this.tolerance;
  }

  announce() {
    if (!this.statusIndicator) return;

    const index = Math.min(this.getCurrentIndex() + 1, this.items.length);
    this.statusIndicator.textContent = `Slide ${index} van ${this.items.length}`;
  }

  onResize = () => {
    this.updateContainerOffset();
    this.updateButtons();
  };

  onScroll = () => {
    this.updateButtons();
    this.updateItemAccessibility(this.getCurrentIndex());

    if (this.supportsSnap) return;

    // JS fallback for scroll snapping
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.snapToClosest();
    }, 120);
  };

  onItemKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.scrollByStep(ACarouselScrollDirectionLeft);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.scrollByStep(ACarouselScrollDirectionRight);
    }
  }

  onItemFocus(index) {
    const scrollPos = this.getItemScrollPosition(index);
    this.animateScrollTo(scrollPos, 300);
    this.updateItemAccessibility(index);
    this.announce();
  }

  /* ---------------------------------------------------------------------
   * Drag / Swipe Handling
   * ------------------------------------------------------------------ */

  onDragStart = (e) => {
    // Don't start drag if clicking on buttons or interactive elements
    if (e.target.closest('button, a, input, select, textarea')) return;

    this.isDragging = true;
    this.startX = this.getPointerX(e);
    this.scrollLeftStart = this.viewport.scrollLeft;

    this.viewport.classList.add('is-dragging');

    // Prevent text selection during drag
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
  };

  onDragMove = (e) => {
    if (!this.isDragging) return;

    const currentX = this.getPointerX(e);
    const deltaX = this.startX - currentX;

    this.viewport.scrollLeft = this.scrollLeftStart + deltaX;

    // Prevent vertical scrolling while dragging horizontally
    if (e.type === 'touchmove' && Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
  };

  onDragEnd = () => {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.viewport.classList.remove('is-dragging');

    // Snap to the nearest item
    // this.onScroll();
    this.announce();
  };

  getPointerX(e) {
    if (e.type.startsWith('touch')) {
      return e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX ?? 0;
    }
    return e.clientX;
  }

  snapToClosest() {
    const index = this.getCurrentIndex();
    const scrollPos = this.getItemScrollPosition(index);
    this.animateScrollTo(scrollPos, 300);
    this.updateItemAccessibility(index);
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  animateScrollTo(targetX, duration = 200) {
    if (this.reducedMotion) {
      this.viewport.scrollLeft = targetX;
      return;
    }

    const startX = this.viewport.scrollLeft;
    const distance = targetX - startX;
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeOutCubic(progress);

      this.viewport.scrollLeft = startX + distance * eased;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  /* ---------------------------------------------------------------------
   * Container Offset Calculation
   *
   * When --a-carousel-container is set, calculate the offset needed on
   * each side so that carousel items align with the page container.
   * Sets --_a-carousel-container-offset used by ::before/::after on the track.
   * ------------------------------------------------------------------ */

  updateContainerOffset() {
    const containerValue = getComputedStyle(this.element)
      .getPropertyValue('--a-carousel-container')
      .trim();

    if (!containerValue) return;

    const containerWidth = parseFloat(containerValue);
    if (!containerWidth || containerWidth <= 0) return;

    const carouselWidth = this.element.offsetWidth;
    const offset = Math.max(24, (carouselWidth - containerWidth) / 2);

    this.element.style.setProperty(
      '--_a-carousel-container-offset',
      `${offset}px`
    );
  }

  // ARIA setup for carousel, viewport, items, controls, and status
  setupAria() {
    if (!this.element.hasAttribute('role')) {
      this.element.setAttribute('role', 'region');
    }
    if (!this.element.hasAttribute('aria-roledescription')) {
      this.element.setAttribute('aria-roledescription', 'carousel');
    }

    if (this.viewport && !this.viewport.id) {
      this.viewport.id = `a-carousel-vp-${Math.random().toString(36).slice(2, 9)}`;
    }

    if (
      this.prev &&
      this.viewport?.id &&
      !this.prev.hasAttribute('aria-controls')
    ) {
      this.prev.setAttribute('aria-controls', this.viewport.id);
    }
    if (
      this.next &&
      this.viewport?.id &&
      !this.next.hasAttribute('aria-controls')
    ) {
      this.next.setAttribute('aria-controls', this.viewport.id);
    }

    if (this.statusIndicator) {
      if (!this.statusIndicator.hasAttribute('role')) {
        this.statusIndicator.setAttribute('role', 'status');
      }
      if (!this.statusIndicator.hasAttribute('aria-live')) {
        this.statusIndicator.setAttribute('aria-live', 'polite');
      }
      if (!this.statusIndicator.hasAttribute('aria-atomic')) {
        this.statusIndicator.setAttribute('aria-atomic', 'true');
      }
    }

    const total = this.items.length;
    this.items.forEach((item, i) => {
      if (!item.hasAttribute('role')) {
        item.setAttribute('role', 'group');
      }
      if (!item.hasAttribute('aria-roledescription')) {
        item.setAttribute('aria-roledescription', 'slide');
      }
      item.setAttribute('aria-label', `Slide ${i + 1} van ${total}`);
    });
  }

  updateItemAccessibility(currentIndex) {
    const total = this.items.length;
    this.items.forEach((item, i) => {
      item.setAttribute('tabindex', i === currentIndex ? '0' : '-1');
      if (i === currentIndex) {
        item.setAttribute('aria-current', 'true');
        item.setAttribute('aria-hidden', 'false');
      } else {
        item.removeAttribute('aria-current');
        item.setAttribute('aria-hidden', 'true');
      }
      item.setAttribute('aria-label', `Slide ${i + 1} van ${total}`);
    });
  }
}

// Initiate module
function ACarouselCreateInstance(element) {
  if (!element.carouselClass) {
    element.carouselClass = new ACarousel(element);
    element.carouselClass.init();
  }
}

// Create new instances on DOM mutation
document
  .querySelectorAll(`${ACarouselSelector}`)
  .forEach(ACarouselCreateInstance);

const ACarouselObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${ACarouselSelector}`)
        .forEach(ACarouselCreateInstance);
    });
  });
});

ACarouselObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
